"use client"

import { useState, useRef, useEffect } from "react"
import { Filter, X, Calendar, Download, ChevronLeft, ChevronRight, ChevronDown, Search } from "lucide-react"
import { Toast } from "primereact/toast"
import API_BASE_URL from "../../Config"
import { ClimbingBoxLoader } from "react-spinners"

const AttendanceRecords = () => {
  const [fromDate, setFromDate] = useState("")
  const [toDate, setToDate] = useState("")
  const [designation, setDesignation] = useState([]) // State for selected designations
  const [dashboardData, setDashboardData] = useState([])
  const [loading, setLoading] = useState(false)
  const [currentPage, setCurrentPage] = useState(1)
  const [itemsPerPage] = useState(15)
  const toast = useRef(null)

  // Dropdown options and search functionality
  const [dropdownOptions, setDropdownOptions] = useState({
    designations: [], // Options for the designation filter
  })
  const [openDropdown, setOpenDropdown] = useState(null)
  const [searchTerms, setSearchTerms] = useState({
    designation: "", // Search term for designation filter
  })

  // Refs for search functionality and scroll position
  const searchInputRef = useRef(null)
  const dropdownScrollRef = useRef(null)
  const scrollPositions = useRef({})

  // Extract user data
  const token = sessionStorage.getItem("token")
  const clientId = sessionStorage.getItem("client_id")

  // Initialize dates with current date
  useEffect(() => {
    const now = new Date()
    const currentDate = now.toISOString().split("T")[0]
    setFromDate(currentDate)
    setToDate(currentDate)
  }, [])

  // Fetch designations on component mount
  useEffect(() => {
    const fetchDesignations = async () => {
      if (!clientId || !token) {
        console.warn("Client ID or Token missing, cannot fetch designations.")
        return
      }

      try {
        const response = await fetch(`${API_BASE_URL}api/get_designation?client_id=${encodeURIComponent(clientId)}`, {
          headers: {
            Authorization: `Bearer ${token}`,
          },
        })

        if (response.ok) {
          const data = await response.json()

          let designationList = []
          if (Array.isArray(data)) {
            designationList = data.map((item) => item.name || item.designation || item.title || item)
          } else if (data.data && Array.isArray(data.data)) {
            designationList = data.data.map((item) => item.name || item.designation || item.title || item)
          } else if (data.designations && Array.isArray(data.designations)) {
            designationList = data.designations.map((item) => item.name || item.designation || item.title || item)
          } else if (data.results && Array.isArray(data.results)) {
            designationList = data.results.map((item) => item.name || item.designation || item.title || item)
          }

          const uniqueDesignations = [...new Set(designationList)].filter(Boolean)

          setDropdownOptions((prev) => ({
            ...prev,
            designations: uniqueDesignations,
          }))
        } else {
          const detailMessage = response.status >= 500 ? "SOMETHING WENT WRONG!!!" : "Failed to fetch designations."
          toast.current?.show({
            severity: "error",
            summary: "Error",
            detail: detailMessage,
            life: 0, // Make sticky
            sticky: true, // Make sticky
          })
        }
      } catch (error) {
        toast.current?.show({
          severity: "error",
          summary: "Network Error",
          detail: "Failed to fetch designations.",
          life: 0, // Make sticky
          sticky: true, // Make sticky
        })
      }
    }

    if (token && clientId) {
      fetchDesignations()
    }
  }, [token, clientId])

  // Pagination calculations
  const totalPages = Math.ceil(dashboardData.length / itemsPerPage)
  const startIndex = (currentPage - 1) * itemsPerPage
  const endIndex = startIndex + itemsPerPage
  const currentData = dashboardData.slice(startIndex, endIndex)

  const handlePageChange = (page) => {
    setCurrentPage(page)
  }

  // Custom dropdown handlers
  const toggleDropdown = (field) => {
    if (openDropdown === field) {
      setOpenDropdown(null)
    } else {
      setOpenDropdown(field)
    }
  }

  const handleSearchTermChange = (field, value) => {
    setSearchTerms((prev) => ({
      ...prev,
      [field]: value,
    }))

    // Maintain focus on the search input after state update
    setTimeout(() => {
      if (searchInputRef.current && openDropdown === field) {
        searchInputRef.current.focus()
      }
    }, 0)
  }

  const handleOptionSelect = (field, option) => {
    // Store current scroll position before state update
    const currentScrollTop = dropdownScrollRef.current?.scrollTop || 0

    if (field === "designation") {
      const currentValues = [...designation]
      const index = currentValues.indexOf(option)

      if (index === -1) {
        currentValues.push(option)
      } else {
        currentValues.splice(index, 1)
      }
      setDesignation(currentValues)
    }

    // Restore scroll position after state update
    setTimeout(() => {
      if (dropdownScrollRef.current) {
        dropdownScrollRef.current.scrollTop = currentScrollTop
      }
    }, 0)
  }

  const handleRemoveOption = (field, option) => {
    if (field === "designation") {
      const currentValues = [...designation]
      const index = currentValues.indexOf(option)
      if (index !== -1) {
        currentValues.splice(index, 1)
        setDesignation(currentValues)
      }
    }
  }

  const handleSelectAllDropdown = (field) => {
    if (field === "designation") {
      const searchTerm = searchTerms.designation
      const filteredOptions = searchTerm
        ? dropdownOptions.designations.filter((option) => option.toLowerCase().includes(searchTerm.toLowerCase()))
        : dropdownOptions.designations

      setDesignation(designation.length === filteredOptions.length ? [] : [...filteredOptions])
    }
  }

  // Custom Dropdown Component (adapted from Repository example)
  const CustomDropdown = ({ label, field, options }) => {
    const searchTerm = searchTerms[field]
    const filteredOptions = searchTerm
      ? options.filter((option) => option.toLowerCase().includes(searchTerm.toLowerCase()))
      : options

    const currentValues = designation // This CustomDropdown is specifically for 'designation'

    // Helper function to get search placeholder text
    const getSearchPlaceholder = (field) => {
      switch (field) {
        case "designation":
          return "Search designations..."
        default:
          return "Search..."
      }
    }

    return (
      <div className="relative w-full">
        <label className="block mb-1 sm:mb-2 text-xs sm:text-sm md:text-base font-medium text-white">{label}</label>
        <div
          className="flex flex-wrap gap-1 min-h-[32px] sm:min-h-[36px] md:min-h-[42px] lg:min-h-[44px] p-1 sm:p-1.5 md:p-2 rounded-lg sm:rounded-xl border cursor-pointer w-full transition-all duration-200 bg-white/10 border-white/20 hover:bg-white/15 overflow-hidden"
          onClick={() => toggleDropdown(field)}
        >
          {currentValues.length > 0 ? (
            currentValues.map((option, idx) => (
              <div
                key={idx}
                className="flex items-center text-xs rounded-full px-1.5 sm:px-2 py-0.5 sm:py-1 bg-white/20 text-white max-w-full"
                style={{ maxWidth: "150px" }}
              >
                <span className="mr-1 truncate flex-1 min-w-0" style={{ maxWidth: "calc(100% - 20px)" }} title={option}>
                  {option}
                </span>
                <X
                  size={10}
                  className="cursor-pointer flex-shrink-0 hover:text-red-400 sm:w-3 sm:h-3 md:w-3.5 md:h-3.5 ml-1"
                  onClick={(e) => {
                    e.stopPropagation()
                    handleRemoveOption(field, option)
                  }}
                />
              </div>
            ))
          ) : (
            <span className="text-xs sm:text-sm md:text-base text-white/60">Select {label}</span>
          )}
          <div className="ml-auto flex items-center flex-shrink-0">
            <ChevronDown
              size={12}
              className={`transition-transform text-white/60 sm:w-3.5 sm:h-3.5 md:w-4 md:h-4 ${
                openDropdown === field ? "rotate-180" : ""
              }`}
            />
          </div>
        </div>

        {openDropdown === field && (
          <>
            <div
              className="fixed inset-0 z-[9998]"
              onClick={(e) => {
                // Don't close if clicking on search input or its container
                if (e.target.closest(".search-container")) {
                  return
                }
                setOpenDropdown(null)
              }}
            />
            <div
              ref={dropdownScrollRef}
              className="absolute mt-1 w-full max-h-32 sm:max-h-40 md:max-h-60 lg:max-h-72 overflow-auto rounded-lg sm:rounded-xl backdrop-blur-md border shadow-lg z-[9999] bg-gray-800 border-white/20"
              onScroll={(e) => {
                // Store scroll position for this field
                scrollPositions.current[field] = e.target.scrollTop
              }}
            >
              {/* Search input */}
              <div className="sticky top-0 bg-gray-900 p-1.5 sm:p-2 border-b border-white/20 search-container">
                <div className="relative">
                  <Search className="absolute left-2 sm:left-3 top-1/2 transform -translate-y-1/2 text-white/60 h-3 w-3 sm:h-4 sm:w-4" />
                  <input
                    ref={searchInputRef}
                    type="text"
                    value={searchTerm}
                    onChange={(e) => handleSearchTermChange(field, e.target.value)}
                    onClick={(e) => e.stopPropagation()}
                    onFocus={(e) => e.stopPropagation()}
                    onKeyDown={(e) => e.stopPropagation()}
                    placeholder={getSearchPlaceholder(field)}
                    className="w-full pl-7 sm:pl-8 md:pl-10 pr-2 sm:pr-3 py-1 sm:py-1.5 md:py-2 bg-white/10 border border-white/20 rounded-md sm:rounded-lg text-white text-xs sm:text-sm focus:outline-none focus:ring-2 focus:ring-orange-500"
                    autoComplete="off"
                  />
                </div>
              </div>

              {/* Select All option */}
              <div className="sticky top-0 backdrop-blur-md border-b p-1 sm:p-1.5 md:p-2 bg-white/20 border-white/20">
                <div
                  className="cursor-pointer rounded-md sm:rounded-lg p-1 sm:p-1.5 md:p-2 transition-colors hover:bg-[#d97925] text-white"
                  onClick={() => handleSelectAllDropdown(field)}
                >
                  <span className="text-xs sm:text-sm">
                    {currentValues.length === filteredOptions.length ? "Deselect All" : "Select All"}
                  </span>
                </div>
              </div>

              {/* Options list */}
              {filteredOptions.length > 0 ? (
                filteredOptions.map((option, idx) => (
                  <div
                    key={idx}
                    className={`cursor-pointer p-1 sm:p-1.5 md:p-2 transition-colors ${
                      currentValues.includes(option) ? "bg-white/15" : ""
                    } hover:bg-[#d97925] text-white`}
                    onClick={() => handleOptionSelect(field, option)}
                  >
                    <div className="flex items-center">
                      <div
                        className={`w-3 h-3 sm:w-4 sm:h-4 mr-1 sm:mr-1.5 md:mr-2 rounded border flex-shrink-0 ${
                          currentValues.includes(option) ? "bg-orange-500 border-orange-500" : "border-white/40"
                        }`}
                      >
                        {currentValues.includes(option) && (
                          <div className="flex items-center justify-center h-full">
                            <svg
                              width="6"
                              height="5"
                              viewBox="0 0 10 8"
                              fill="none"
                              xmlns="http://www.w3.org/2000/svg"
                              className="sm:w-2 sm:h-1.5 md:w-2.5 md:h-2"
                            >
                              <path
                                d="M9 1L3.5 6.5L1 4"
                                stroke="white"
                                strokeWidth="2"
                                strokeLinecap="round"
                                strokeLinejoin="round"
                              />
                            </svg>
                          </div>
                        )}
                      </div>
                      <span className="text-xs sm:text-sm truncate" title={option}>
                        {option}
                      </span>
                    </div>
                  </div>
                ))
              ) : (
                <div className="p-2 sm:p-3 md:p-4 text-center text-white/60 text-xs sm:text-sm">
                  No matching designations found
                </div>
              )}
            </div>
          </>
        )}
      </div>
    )
  }

  // Auto-focus search input when dropdown opens
  useEffect(() => {
    if (openDropdown && searchInputRef.current) {
      setTimeout(() => {
        searchInputRef.current?.focus()
      }, 100)
    }
  }, [openDropdown])

  // Restore scroll position when dropdown reopens
  useEffect(() => {
    if (openDropdown && dropdownScrollRef.current && scrollPositions.current[openDropdown]) {
      setTimeout(() => {
        if (dropdownScrollRef.current) {
          dropdownScrollRef.current.scrollTop = scrollPositions.current[openDropdown]
        }
      }, 100)
    }
  }, [openDropdown])

  const handleFilter = async () => {
    if (!fromDate || !toDate) {
      toast.current?.show({
        severity: "warn",
        summary: "Date Required",
        detail: "Please select both from and to dates.",
        life: 0, // Make sticky
        sticky: true, // Make sticky
      })
      return
    }

    setLoading(true)
    setCurrentPage(1) // Reset to first page when filtering

    try {
      // Build query parameters
      let filterQuery = `client_id=${encodeURIComponent(clientId)}&from_date=${encodeURIComponent(fromDate)}&to_date=${encodeURIComponent(toDate)}`

      // Add designation filter if selected
      if (designation.length > 0) {
        filterQuery += `&designation=${encodeURIComponent(designation.join(","))}`
      }

      const response = await fetch(`${API_BASE_URL}api/attendance_summary/?${filterQuery}`, {
        headers: {
          Authorization: `Bearer ${token}`,
        },
      })

      if (response.ok) {
        const data = await response.json()

        // Try different possible data structures
        let attendanceData = []

        if (data.daily_summary && Array.isArray(data.daily_summary)) {
          attendanceData = data.daily_summary
        } else if (data.attendance_data && Array.isArray(data.attendance_data)) {
          attendanceData = data.attendance_data
        } else if (data.data && Array.isArray(data.data)) {
          attendanceData = data.data
        } else if (Array.isArray(data)) {
          attendanceData = data
        } else if (data.results && Array.isArray(data.results)) {
          attendanceData = data.results
        } else if (data.attendance && Array.isArray(data.attendance)) {
          attendanceData = data.attendance
        } else {
          // Look for any array in the response
          for (const key in data) {
            if (Array.isArray(data[key])) {
              attendanceData = data[key]
              break
            }
          }
        }

        setDashboardData(attendanceData)
      } else {
        const detailMessage = response.status >= 500 ? "SOMETHING WENT WRONG!!!" : "Failed to fetch attendance data."
        toast.current?.show({
          severity: "error",
          summary: "Filter Error",
          detail: detailMessage,
          life: 0, // Make sticky
          sticky: true, // Make sticky
        })
      }
    } catch (error) {
      toast.current?.show({
        severity: "error",
        summary: "Network Error",
        detail: error.message || "Failed to fetch attendance data.",
        life: 0, // Make sticky
        sticky: true, // Make sticky
      })
    } finally {
      setLoading(false)
    }
  }

  const handleDownload = async () => {
    if (!fromDate || !toDate) {
      toast.current?.show({
        severity: "warn",
        summary: "Date Required",
        detail: "Please filter data first.",
        life: 0, // Make sticky
        sticky: true, // Make sticky
      })
      return
    }

    setLoading(true)

    try {
      // Build query parameters for download
      let query = `client_id=${encodeURIComponent(clientId)}&from_date=${encodeURIComponent(fromDate)}&to_date=${encodeURIComponent(toDate)}&file_type=excel`

      // Add designation filter if selected
      if (designation.length > 0) {
        query += `&designation=${encodeURIComponent(designation.join(","))}`
      }

      const response = await fetch(`${API_BASE_URL}api/attendance_summary/?${query}`, {
        method: "GET",
        headers: {
          Authorization: `Bearer ${token}`,
        },
      })

      if (response.ok) {
        // Check if response is actually an Excel file
        const contentType = response.headers.get("content-type")

        if (contentType && contentType.includes("application/json")) {
          // Backend returned JSON instead of Excel file
          toast.current?.show({
            severity: "error",
            summary: "Download Error",
            detail: "Backend returned data instead of Excel file. Please check your backend configuration.",
            life: 0, // Make sticky
            sticky: true, // Make sticky
          })
          return
        }

        // Get the file as blob and download directly
        const blob = await response.blob()

        // Check if blob is empty
        if (blob.size === 0) {
          toast.current?.show({
            severity: "error",
            summary: "Download Error",
            detail: "Downloaded file is empty.",
            life: 0, // Make sticky
            sticky: true, // Make sticky
          })
          return
        }

        const url = window.URL.createObjectURL(blob)
        const link = document.createElement("a")
        link.href = url

        // Try to get filename from Content-Disposition header
        const contentDisposition = response.headers.get("Content-Disposition")
        if (contentDisposition && contentDisposition.includes("filename=")) {
          const filename = contentDisposition.split("filename=")[1].replace(/"/g, "")
          link.download = filename
        } else {
          link.download = "attendance_report.xlsx"
        }

        link.click()
        window.URL.revokeObjectURL(url)
      } else {
        const detailMessage = response.status >= 500 ? "SOMETHING WENT WRONG!!!" : "Failed to download report."
        toast.current?.show({
          severity: "error",
          summary: "Download Error",
          detail: detailMessage,
          life: 0, // Make sticky
          sticky: true, // Make sticky
        })
      }
    } catch (error) {
      toast.current?.show({
        severity: "error",
        summary: "Download Error",
        detail: error.message || "Failed to download attendance report.",
        life: 0, // Make sticky
        sticky: true, // Make sticky
      })
    } finally {
      setLoading(false)
    }
  }

  const handleClear = () => {
    const now = new Date()
    const currentDate = now.toISOString().split("T")[0]
    setFromDate(currentDate)
    setToDate(currentDate)
    setDesignation([]) // Clear selected designations
    setDashboardData([])
    setCurrentPage(1)
    setSearchTerms({ designation: "" }) // Clear designation search term
  }

  return (
    <div className="relative min-h-screen">
      {/* Loader Overlay */}
      {loading && (
        <div className="fixed inset-0 bg-black bg-opacity-50 z-50 flex items-center justify-center">
          <ClimbingBoxLoader color="#d97925" size={20} />
        </div>
      )}

      <div className="min-h-screen bg-cover bg-center p-3 sm:p-6" style={{ backgroundImage: "url('/background.jpg')" }}>
        <Toast ref={toast} position="top-right" />

        <div className="max-w-7xl mx-auto">
          {/* Filters Section - Mobile Responsive */}
          {/* Removed overflow-hidden and added z-index to allow dropdown to overlap */}
          <div className="backdrop-blur-xl bg-gradient-to-br from-white/15 to-white/5 border border-white/30 rounded-2xl shadow-2xl p-3 sm:p-4 mb-4 sm:mb-6 relative z-20">
            {/* Decorative elements - smaller on mobile */}
            <div className="absolute top-0 right-0 w-16 h-16 sm:w-20 sm:h-20 bg-gradient-to-br from-[#d97925]/20 to-transparent rounded-full blur-xl"></div>
            <div className="absolute bottom-0 left-0 w-12 h-12 sm:w-16 sm:h-16 bg-gradient-to-tr from-blue-500/20 to-transparent rounded-full blur-xl"></div>

            <div className="relative z-10">
              <div className="flex items-center gap-2 mb-3 sm:mb-4">
                <div className="p-1.5 rounded-lg bg-gradient-to-br from-[#d97925] to-[#e8842d] shadow-lg">
                  <Filter className="text-white" size={18} />
                </div>
                <h2 className="text-base sm:text-lg font-bold text-white bg-gradient-to-r from-white to-white/80 bg-clip-text">
                  Filters
                </h2>
              </div>

              <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-3 sm:gap-4 mb-3 sm:mb-4">
                {/* Designation Filter - Now First */}
                <CustomDropdown label="Designation" field="designation" options={dropdownOptions.designations} />

                {/* From Date */}
                <div className="space-y-2">
                  <label className="block text-xs font-semibold text-white flex items-center gap-2">
                    <div className="p-1 rounded-md bg-[#d97925]/20">
                      <Calendar size={12} className="text-[#d97925]" />
                    </div>
                    From Date
                  </label>
                  <div className="relative">
                    <input
                      type="date"
                      value={fromDate}
                      onChange={(e) => setFromDate(e.target.value)}
                      className="w-full px-3 py-2.5 rounded-lg backdrop-blur-md bg-white/10 border-2 border-white/20 text-white text-sm font-medium placeholder-white/50 hover:border-[#d97925]/50 hover:bg-[#d97925]/10 focus:outline-none focus:border-[#d97925] focus:bg-[#d97925]/15 focus:ring-2 focus:ring-[#d97925]/20 transition-all duration-300 shadow-lg"
                      style={{ colorScheme: "dark" }}
                    />
                  </div>
                </div>

                {/* To Date */}
                <div className="space-y-2">
                  <label className="block text-xs font-semibold text-white flex items-center gap-2">
                    <div className="p-1 rounded-md bg-[#d97925]/20">
                      <Calendar size={12} className="text-[#d97925]" />
                    </div>
                    To Date
                  </label>
                  <div className="relative">
                    <input
                      type="date"
                      value={toDate}
                      onChange={(e) => setToDate(e.target.value)}
                      className="w-full px-3 py-2.5 rounded-lg backdrop-blur-md bg-white/10 border-2 border-white/20 text-white text-sm font-medium placeholder-white/50 hover:border-[#d97925]/50 hover:bg-[#d97925]/10 focus:outline-none focus:border-[#d97925] focus:bg-[#d97925]/15 focus:ring-2 focus:ring-[#d97925]/20 transition-all duration-300 shadow-lg"
                      style={{ colorScheme: "dark" }}
                    />
                  </div>
                </div>

                {/* Buttons - Mobile responsive */}
                <div className="flex flex-row sm:flex-col gap-2 justify-end sm:col-span-1 col-span-2">
                  <button
                    onClick={handleFilter}
                    className="flex-1 sm:flex-none px-4 py-2.5 rounded-lg bg-gradient-to-r from-[#d97925] to-[#e8842d] text-white font-bold flex items-center justify-center gap-2 hover:-translate-y-1 hover:shadow-lg hover:shadow-[#d97925]/40 active:translate-y-0 transition-all duration-300 backdrop-blur-md border border-[#d97925]/30 relative overflow-hidden group text-sm"
                  >
                    <Filter size={16} />
                    <span className="relative z-10">Filter</span>
                  </button>
                  <button
                    onClick={handleClear}
                    className="flex-1 sm:flex-none px-4 py-2.5 rounded-lg text-white font-bold border-2 border-white/30 hover:border-red-400 hover:bg-red-400/20 transition-all duration-300 flex items-center justify-center gap-2 backdrop-blur-md relative overflow-hidden group text-sm"
                  >
                    <X size={16} />
                    <span className="relative z-10">Clear</span>
                  </button>
                </div>
              </div>
            </div>
          </div>

          {/* Attendance Table - Mobile Responsive */}
          <div className="backdrop-blur-xl bg-gradient-to-br from-white/15 to-white/5 border border-white/30 rounded-2xl sm:rounded-3xl shadow-2xl overflow-hidden">
            {/* Header - Mobile responsive */}
            <div className="p-3 sm:p-6 border-b border-white/20 flex flex-col sm:flex-row justify-between items-start sm:items-center gap-3 sm:gap-0">
              <h2 className="text-lg sm:text-2xl font-bold text-white flex items-center gap-2 sm:gap-3">
                <div className="p-1.5 sm:p-2 rounded-xl bg-gradient-to-br from-[#d97925] to-[#e8842d] shadow-lg">
                  <Calendar className="text-white" size={20} />
                </div>
                <span className="text-sm sm:text-2xl">Attendance Summary ({dashboardData.length} records)</span>
              </h2>
              <button
                onClick={handleDownload}
                disabled={dashboardData.length === 0}
                className="w-full sm:w-auto px-3 sm:px-4 py-2 rounded-xl bg-gradient-to-r from-green-500 to-green-600 text-white font-semibold flex items-center justify-center gap-2 hover:-translate-y-1 hover:shadow-lg hover:shadow-green-500/30 active:translate-y-0 transition-all duration-300 disabled:opacity-50 disabled:cursor-not-allowed disabled:hover:translate-y-0 text-sm"
              >
                <Download size={16} />
                Download Excel
              </button>
            </div>

            {/* Mobile Card View for small screens */}
            <div className="block sm:hidden">
              {currentData.length > 0 ? (
                <div className="p-3 space-y-3">
                  {currentData.map((row, index) => (
                    <div
                      key={startIndex + index}
                      className="bg-white/10 backdrop-blur-md rounded-lg p-4 border border-white/20"
                    >
                      <div className="grid grid-cols-2 gap-3 text-sm">
                        <div>
                          <span className="text-white/70 font-medium">Date:</span>
                          <div className="text-white font-bold">
                            {row.date || row.Date || row.attendance_date || "-"}
                          </div>
                        </div>
                        <div>
                          <span className="text-white/70 font-medium">Total:</span>
                          <div className="text-white font-bold">
                            {row["Total Employees"] ||
                              row.total_employees ||
                              row.TotalEmployees ||
                              row.totalEmployees ||
                              0}
                          </div>
                        </div>
                        <div>
                          <span className="text-white/70 font-medium">Present:</span>
                          <div className="text-green-400 font-bold">
                            {row.Present || row.present || row.presentCount || 0}
                          </div>
                        </div>
                        <div>
                          <span className="text-white/70 font-medium">Half Present:</span>
                          <div className="text-yellow-400 font-bold">
                            {row["Half Present"] || row.half_present || row.Half_Present || row.halfPresent || 0}
                          </div>
                        </div>
                        <div>
                          <span className="text-white/70 font-medium">Short Duration:</span>
                          <div className="text-orange-400 font-bold">
                            {row["Short Duration"] ||
                              row.short_duration ||
                              row.Short_Duration ||
                              row.shortDuration ||
                              0}
                          </div>
                        </div>
                        <div>
                          <span className="text-white/70 font-medium">Absent:</span>
                          <div className="text-red-400 font-bold">
                            {row.Absent || row.absent || row.absentCount || 0}
                          </div>
                        </div>
                        <div>
                          <span className="text-white/70 font-medium">Leave:</span>
                          <div className="text-blue-400 font-bold">{row.Leave || row.leave || row.leaveCount || 0}</div>
                        </div>
                      </div>
                    </div>
                  ))}
                </div>
              ) : (
                <div className="p-6 text-center text-white/70">
                  {loading
                    ? "Loading data..."
                    : dashboardData.length === 0
                      ? "No data available. Please apply filters to view attendance data."
                      : "No data to display on this page."}
                </div>
              )}
            </div>

            {/* Desktop Table View */}
            <div className="hidden sm:block overflow-x-auto">
              <table className="w-full">
                <thead>
                  <tr className="border-b border-white/20 bg-white/5">
                    <th className="px-4 lg:px-6 py-4 text-center text-sm font-bold text-white">Date</th>
                    <th className="px-4 lg:px-6 py-4 text-center text-sm font-bold text-white">Total Employees</th>
                    <th className="px-4 lg:px-6 py-4 text-center text-sm font-bold text-white">Present</th>
                    <th className="px-4 lg:px-6 py-4 text-center text-sm font-bold text-white">Half Present</th>
                    <th className="px-4 lg:px-6 py-4 text-center text-sm font-bold text-white">Absent</th>
                  </tr>
                </thead>
                <tbody>
                  {currentData.length > 0 ? (
                    currentData.map((row, index) => (
                      <tr
                        key={startIndex + index}
                        className="border-b border-white/10 hover:bg-white/10 transition-colors duration-200"
                      >
                        <td className="px-4 lg:px-6 py-4 text-sm text-white font-medium text-center">
                          {row.date || row.Date || row.attendance_date || "-"}
                        </td>
                        <td className="px-4 lg:px-6 py-4 text-sm text-white text-center">
                          {row["Total Employees"] ||
                            row.total_employees ||
                            row.TotalEmployees ||
                            row.totalEmployees ||
                            0}
                        </td>
                        <td className="px-4 lg:px-6 py-4 text-sm text-green-400 font-bold text-center">
                          {row.Present || row.present || row.presentCount || 0}
                        </td>
                        <td className="px-4 lg:px-6 py-4 text-sm text-yellow-400 font-bold text-center">
                          {row["Half Present"] || row.half_present || row.Half_Present || row.halfPresent || 0}
                        </td>
                        <td className="px-4 lg:px-6 py-4 text-sm text-red-400 font-bold text-center">
                          {row.Absent || row.absent || row.absentCount || 0}
                        </td>
                      </tr>
                    ))
                  ) : (
                    <tr>
                      <td colSpan="7" className="px-4 lg:px-6 py-12 text-center text-white/70 text-lg">
                        {loading
                          ? "Loading data..."
                          : dashboardData.length === 0
                            ? "No data available. Please apply filters to view attendance data."
                            : "No data to display on this page."}
                      </td>
                    </tr>
                  )}
                </tbody>
              </table>
            </div>

            {/* Pagination - Mobile Responsive */}
            {dashboardData.length > 0 && (
              <div className="p-3 sm:p-6 border-t border-white/20 bg-white/5">
                <div className="flex flex-col sm:flex-row items-center justify-between gap-3 sm:gap-0">
                  <div className="text-white/70 text-xs sm:text-sm order-2 sm:order-1">
                    Showing {startIndex + 1} to {Math.min(endIndex, dashboardData.length)} of {dashboardData.length}{" "}
                    entries
                  </div>

                  <div className="flex items-center gap-1 sm:gap-2 order-1 sm:order-2">
                    <button
                      onClick={() => handlePageChange(currentPage - 1)}
                      disabled={currentPage === 1}
                      className="p-2 rounded-lg bg-white/10 text-white hover:bg-white/20 disabled:opacity-50 disabled:cursor-not-allowed transition-all duration-200"
                    >
                      <ChevronLeft size={16} />
                    </button>

                    {/* Show fewer page numbers on mobile */}
                    {Array.from({ length: Math.min(totalPages, 5) }, (_, i) => {
                      let page
                      if (totalPages <= 5) {
                        page = i + 1
                      } else {
                        // Show pages around current page
                        const start = Math.max(1, currentPage - 2)
                        const end = Math.min(totalPages, start + 4)
                        page = start + i
                        if (page > end) return null
                      }

                      return (
                        <button
                          key={page}
                          onClick={() => handlePageChange(page)}
                          className={`px-2 sm:px-3 py-2 rounded-lg text-xs sm:text-sm font-medium transition-all duration-200 ${
                            currentPage === page
                              ? "bg-gradient-to-r from-[#d97925] to-[#e8842d] text-white shadow-lg"
                              : "bg-white/10 text-white hover:bg-white/20"
                          }`}
                        >
                          {page}
                        </button>
                      )
                    })}

                    <button
                      onClick={() => handlePageChange(currentPage + 1)}
                      disabled={currentPage === totalPages}
                      className="p-2 rounded-lg bg-white/10 text-white hover:bg-white/20 disabled:opacity-50 disabled:cursor-not-allowed transition-all duration-200"
                    >
                      <ChevronRight size={16} />
                    </button>
                  </div>
                </div>
              </div>
            )}

            {/* Legend - Mobile Responsive */}
            {dashboardData.length > 0 && (
              <div className="p-3 sm:p-4 border-t border-white/20 bg-white/5">
                <div className="grid grid-cols-2 sm:flex sm:flex-wrap gap-2 sm:gap-4 justify-center text-xs sm:text-sm">
                  <div className="flex items-center gap-2">
                    <div className="w-3 h-3 bg-green-400 rounded-full shadow-lg"></div>
                    <span className="text-white/70 font-medium">Present</span>
                  </div>
                  <div className="flex items-center gap-2">
                    <div className="w-3 h-3 bg-yellow-400 rounded-full shadow-lg"></div>
                    <span className="text-white/70 font-medium">Half Present</span>
                  </div>
                  <div className="flex items-center gap-2">
                    <div className="w-3 h-3 bg-orange-400 rounded-full shadow-lg"></div>
                    <span className="text-white/70 font-medium">Short Duration</span>
                  </div>
                  <div className="flex items-center gap-2">
                    <div className="w-3 h-3 bg-red-400 rounded-full shadow-lg"></div>
                    <span className="text-white/70 font-medium">Absent</span>
                  </div>
                  <div className="flex items-center gap-2 col-span-2 sm:col-span-1 justify-center sm:justify-start">
                    <div className="w-3 h-3 bg-blue-400 rounded-full shadow-lg"></div>
                    <span className="text-white/70 font-medium">Leave</span>
                  </div>
                </div>
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  )
}

export default AttendanceRecords
